/**
 * Helpers for deep-link redirect param sanitisation and map pre-selection.
 */

/**
 * Allowed path prefixes that can be used as post-login redirect targets.
 * Only internal app routes are accepted — never external URLs.
 */
const ALLOWED_PREFIXES = ['/(app)/', '/(auth)/'] as const

/**
 * Parse and sanitise a raw `redirect` query param value.
 *
 * Returns the param unchanged if it starts with an allowed internal prefix,
 * otherwise returns null so callers can fall back to the default destination.
 * This prevents open-redirect attacks where an attacker-controlled URL could
 * be passed as the redirect target.
 */
export function parseRedirectParam(raw: string | string[] | undefined): string | null {
  if (!raw) return null
  const s = Array.isArray(raw) ? raw[0] : raw
  if (!s) return null
  for (const prefix of ALLOWED_PREFIXES) {
    if (s.startsWith(prefix)) return s
  }
  return null
}

/**
 * Reconstruct the full internal route for a protected (app) screen from the
 * `usePathname()` value. Router pathnames omit the `(app)` group segment (e.g.
 * `/games/123`), but parseRedirectParam's allowlist expects the `/(app)/`
 * prefix — so the guard uses this to build the value it persists / bounces with.
 * Exported + pure for testing.
 */
export function buildProtectedFullPath(pathname: string, blockTarget?: string, messageId?: string, kind?: string, gameId?: string): string {
  const path = pathname.startsWith('/') ? `/(app)${pathname}` : `/(app)/${pathname}`
  // Preserve the one safety action parameter across login, not arbitrary query
  // parameters (which may contain redirects or unrelated sensitive data).
  if (pathname !== '/settings/blocked-users' || !blockTarget || blockTarget.length > 128) return path
  const context = messageId && messageId.length <= 128 && (kind === 'dm' || kind === 'chat')
    ? `&messageId=${encodeURIComponent(messageId)}&kind=${kind}` : ''
  const game = gameId && gameId.length <= 128 ? `&gameId=${encodeURIComponent(gameId)}` : ''
  return `${path}?block=${encodeURIComponent(blockTarget)}${context}${game}`
}

/**
 * Decide where to send a just-authenticated user, given a persisted post-login
 * target and where they currently are. The single source of truth for the
 * `(app)` guard's resume step:
 *   - no target                         → null (nothing to resume)
 *   - target fails the internal allowlist → null (open-redirect safety)
 *   - target is where we already are      → null (avoid a redundant navigation)
 *   - otherwise                           → the validated target
 * Pure + exported for testing.
 */
export function resolvePostAuthRedirect(
  target: string | null | undefined,
  currentFullPath: string,
): string | null {
  const safe = parseRedirectParam(target ?? undefined)
  if (!safe) return null
  if (safe === currentFullPath) return null
  return safe
}

/**
 * Resolve the initial mapId for the new-game screen (games/new).
 *
 * Accepts the raw query param (may be undefined / an array) and the list of
 * valid map ids loaded from the API. Returns the query param value when it
 * is present in the valid set, otherwise returns the default `'carpathia'`.
 */
export function resolveInitialMapId(
  queryMapId: string | string[] | undefined,
  validMapIds: string[],
  defaultMapId = 'carpathia',
): string {
  const id = Array.isArray(queryMapId) ? queryMapId[0] : queryMapId
  if (id && validMapIds.includes(id)) return id
  return defaultMapId
}
